import { Avatar, Button, ButtonLink, EmptyState, UsersIcon } from "@rhema/ui";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toneFor } from "../data/view";
import { useApi, useClient } from "../session/session";

type Preview = { chat_id: string; title: string; about: string; member_count: number };

// Opened from a group invite link, /join/<code>.
export function JoinScreen({ code }: { code: string }) {
  const api = useApi();
  const client = useClient();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .previewInvite(code)
      .then((p) => live && setPreview(p))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [api, code]);

  const join = async () => {
    setBusy(true);
    setError(null);
    try {
      const chat = await api.joinInvite(code);
      await client.refreshChat(chat.id);
      navigate(`/chats/${chat.id}`, { replace: true });
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  if (error && !preview) {
    return (
      <EmptyState
        icon={<UsersIcon size={26} />}
        title="This invite does not work"
        className="flex-1"
        action={<ButtonLink href="/chats">Go to your chats</ButtonLink>}
      >
        {error}
      </EmptyState>
    );
  }
  if (!preview) return <EmptyState title="Opening the invite" className="flex-1" />;

  const already = Boolean(client.getState().chats[preview.chat_id]);

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-12 text-center">
      <Avatar name={preview.title} tone={toneFor(preview.chat_id)} size={96} shape="rounded" serif />
      <h1 className="m-0 font-serif text-[30px] font-medium tracking-[-0.02em]">{preview.title}</h1>
      <p className="m-0 text-sm text-ink-secondary">
        Group · {preview.member_count} {preview.member_count === 1 ? "person" : "people"}
      </p>
      {preview.about && <p className="m-0 max-w-sm text-[15px] leading-normal">{preview.about}</p>}
      {error && (
        <p role="alert" className="m-0 text-sm font-semibold text-scarlet">
          {error}
        </p>
      )}
      {already ? (
        <ButtonLink href={`/chats/${preview.chat_id}`} size="lg">
          Open the group
        </ButtonLink>
      ) : (
        <Button size="lg" disabled={busy} onClick={() => void join()}>
          {busy ? "Joining" : "Join the group"}
        </Button>
      )}
      <p className="m-0 max-w-sm text-[13px] text-ink-secondary">
        You will see messages sent after you join. Earlier messages stay with the people who were there.
      </p>
    </div>
  );
}
